import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import {
  checkMoneyDataForTable,
  checkNonMoneyDataForTable,
  trimObject,
  dateTimeFormatter,
} from "utils/utils";
import { queryDisputeList } from "../model";
import s from "./DisputeList.module.scss";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";

import { searchErrorHandler } from "utils/formValidation";
import moment from "moment";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import Datepicker from "components/Input/Datepicker/Datepicker";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import ChargebackStatusOptions from "utils/CompliancePageOptions/ChargebackStatusOptions";
import Compliance from "routes/compliance";
import dayjs from "dayjs";
import useRefresh from "utils/useRefresh/useRefresh";

const PAGE_LOCALE_KEY = "page.compliance.dispute.list.";

const INPUT_FIELD_INIT_STATE = {
  id: "",
  paymentId: "",
  // merchantId: "",
  reportedStartDate: null,
  reportedEndDate: null,
  status: "",
};

const INPUT_FIELD_ID_OVERRIDE_STATE = {
  paymentId: "",
  // merchantId: "",
  reportedStartDate: "",
  reportedEndDate: "",
  status: "",
};

const ERROR_INIT_STATE = {
  reportedStartDate: false,
  invalidReportedStartDate: false,
  reportedEndDate: false,
  invalidReportedEndDate: false,
  invalidReportedDateRange: false,
  tooLargeReportedDateRange: false,
};

const DisputeList = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "disputeList";

  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const isInitRef = useRef(true);

  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState(
    INPUT_FIELD_INIT_STATE,
  );
  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);
  const [isIdInput, setIsIdInput] = useState(false);

  const [columnsArray, setColumnsArray] = useState([]);

  useRefresh({
    pagePath: path,
    preventRefresh: loading,
    redirectFn,
    effectFn: () => handleSearch(),
  });

  useEffect(() => {
    if (searchFieldInput.id !== "" && !isIdInput) {
      setIsIdInput(true);
      setSearchFieldInput({
        ...searchFieldInput,
        ...INPUT_FIELD_ID_OVERRIDE_STATE,
      });
    } else if (searchFieldInput.id === "" && isIdInput) {
      setIsIdInput(false);
      setSearchFieldInput(INPUT_FIELD_INIT_STATE);
    }
  }, [searchFieldInput.id]);

  useEffect(() => {
    let isOudated = false;
    const getIsOudated = () => isOudated;

    initialFetchList(getIsOudated);

    return () => {
      isOudated = true;
    };
  }, []);

  const initialFetchList = async (getIsOudated) => {
    setLoading(true);
    const apiResponse = await queryDisputeList(
      trimObject({
        page: 0,
        size: pageSizeInfoRef.current.pagesize,
        ...INPUT_FIELD_INIT_STATE,
      }),
    );
    if (getIsOudated && getIsOudated()) return;

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            isInitRef.current = false;
            setDatasource(responseBody);
            setCurrentPage(parseInt(responseBody.page));
            setPageSize(parseInt(responseBody.size));
            pageSizeInfoRef.current.currentpage = parseInt(responseBody.page);
            pageSizeInfoRef.current.pagesize = parseInt(responseBody.size);
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
          }
        }
      }
    }

    setSearch(false);
    setLoading(false);
  };

  const resetInputField = () => {
    setSearchFieldInput(INPUT_FIELD_INIT_STATE);
  };

  const resetError = () => {
    setError(ERROR_INIT_STATE);
  };

  const handleReset = () => {
    isInitRef.current = true;
    setIsIdInput(false);
    resetInputField();
    resetError();
    initialFetchList();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      const { errorObj, hasError } = searchErrorHandler(searchFieldInput, [
        (input) =>
          input.reportedStartDate &&
          !input.reportedEndDate && {
            reportedEndDate: true,
          },
        (input) =>
          moment(input.reportedEndDate).isAfter(moment().endOf("day")) && {
            invalidReportedEndDate: true,
          },
        (input) =>
          input.reportedEndDate &&
          !input.reportedStartDate && {
            reportedStartDate: true,
          },
        (input) =>
          moment(input.reportedStartDate).isBefore(moment("2022-01-01")) && {
            invalidReportedStartDate: true,
          },
        (input) =>
          moment(input.reportedStartDate).isAfter(input.reportedEndDate) && {
            invalidReportedDateRange: true,
          },
        (input) => {
          const startDate = moment(input.reportedStartDate).startOf("day");
          const endDate = moment(input.reportedEndDate).startOf("day");
          if (endDate.diff(startDate, "month", true) > 6) {
            return { tooLargeReportedDateRange: true };
          }
        },
      ]);
      setError(errorObj);
      if (hasError) return;

      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);

    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };

    const apiResponse = await queryDisputeList(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            setDatasource(responseBody);
            setCurrentPage(parseInt(responseBody.page));
            setPageSize(parseInt(responseBody.size));
            pageSizeInfoRef.current.currentpage = parseInt(responseBody.page);
            pageSizeInfoRef.current.pagesize = parseInt(responseBody.size);
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
          }
        }
      }
    }

    setSearch(true);
    setSearching(false);
  };

  // ===================== search field function start ===================== //

  const textInputOnChange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const isInputDisabled = (field) => {
    if (field !== "id" && searchFieldInput.id !== "") return true;
  };

  const reportedDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    let date = moment(rawDate).isValid()
      ? moment(rawDate)
      : e !== null
        ? ""
        : null;
    if (type === "start") {
      date = moment(rawDate).isValid()
        ? date.startOf("day").toISOString()
        : date;
      setError({
        ...error,
        reportedStartDate: false,
        invalidReportedDateRange: false,
      });
      setSearchFieldInput({ ...searchFieldInput, reportedStartDate: date });
    } else if (type === "end") {
      date = moment(rawDate).isValid() ? date.endOf("day").toISOString() : date;
      setError({
        ...error,
        reportedEndDate: false,
        invalidReportedDateRange: false,
      });
      setSearchFieldInput({ ...searchFieldInput, reportedEndDate: date });
    } else {
      return;
    }
  };
  // ===================== search field function end ===================== //

  // ===================== column display function ===================== //
  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const dateFormatValidator = (dateValue) => {
    const isValidDateFormat = moment(dateValue).isValid();
    return isValidDateFormat;
  };

  const dateFormatter = (date) => {
    if (date === "-") return date;
    return moment(date).format("YYYY-MM-DD");
  };

  const columns = [
    {
      key: "id",
      title: t(PAGE_LOCALE_KEY + "columnTitle.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "reported",
      title: t(PAGE_LOCALE_KEY + "columnTitle.reported"),
      sorter: false,
      render: (data, key) => {
        return dateFormatter(checkNonMoneyDataForTable(data, key));
      },
    },
    {
      key: "chargebackCode",
      title: t(PAGE_LOCALE_KEY + "columnTitle.chargebackCode"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "daysLeft",
      title: t(PAGE_LOCALE_KEY + "columnTitle.daysLeft"),
      sorter: false,
      render: (data, key) => {
        if (data[key] === "") return "-";
        return data[key] ?? "-";
      },
    },
    {
      key: "paymentId",
      title: t(PAGE_LOCALE_KEY + "columnTitle.paymentId"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "chargebackAmount",
      title: t(PAGE_LOCALE_KEY + "columnTitle.chargebackAmount"),
      sorter: false,
      type: "amount",
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      key: "transactionDate",
      title: t(PAGE_LOCALE_KEY + "columnTitle.transactionDate"),
      sorter: false,
      render: (data, key) => {
        return dateTimeFormatter(checkNonMoneyDataForTable(data, key));
      },
    },
    {
      key: "cardType",
      title: t(PAGE_LOCALE_KEY + "columnTitle.cardType"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "paymentGateway",
      title: t(PAGE_LOCALE_KEY + "columnTitle.paymentGateway"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "status",
      title: t(PAGE_LOCALE_KEY + "columnTitle.status"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "statusUpdated",
      title: t(PAGE_LOCALE_KEY + "columnTitle.statusUpdated"),
      sorter: false,
      render: (data, key) => {
        return dateTimeFormatter(checkNonMoneyDataForTable(data, key));
      },
    },

    // {
    //   key: "merchant",
    //   title: t(PAGE_LOCALE_KEY + "columnTitle.merchant"),
    //   sorter: false,
    //   render: (data, key) => {
    //     return checkNonMoneyDataForTable(data, key);
    //   },
    // },
    // {
    //   key: "transactionAmount",
    //   title: t(PAGE_LOCALE_KEY + "columnTitle.transactionAmount"),
    //   sorter: false,
    //   type: "amount",
    //   render: (data, key) => {
    //     return checkMoneyDataForTable(data, key);
    //   },
    // },
    // {
    //   key: "reason",
    //   title: t(PAGE_LOCALE_KEY + "columnTitle.reason"),
    //   sorter: false,
    //   render: (data, key) => {
    //     return checkNonMoneyDataForTable(data, key);
    //   },
    // },

    // For future use if there is a need to add other actions except onClick for detail page.
    // {
    //   key: "action",
    //   title: t(PAGE_LOCALE_KEY + "columnTitle.action"),
    //   sorter: false,
    //   render: (data) => {
    //     const { id } = data;
    //     const path = "/compliance/disputeDetail/DisputeDetail";
    //     const payload = {
    //       name: Compliance.find((tab) => tab.path === path).tabName + data.id,
    //       path: path,
    //       state: {
    //         id: id,
    //         createFlag: false,
    //       },
    //     };
    //     return (
    //       <div className={s.actionBtnContainer}>
    //         <button onClick={() => redirectFn("redirectPage", payload)}>
    //           {t(PAGE_LOCALE_KEY + "columnTitle.actionContent.1")}
    //         </button>
    //       </div>
    //     );
    //   },
    // },
  ].map((column, index) => ({ ...column, index: index }));

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 30, 40],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId + "0"}>
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.id.label")}
            placeholder={t(PAGE_LOCALE_KEY + "search.id.placeholder")}
            value={searchFieldInput.id}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "id")}
            onfocusFn={() => textInputOnFocus("id")}
            onblurFn={() => textInputOnBlur("id")}
            sxParams={{ textFocus: focus.id }}
            disabled={isInputDisabled("id")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.paymentId.label")}
            placeholder={t(PAGE_LOCALE_KEY + "search.paymentId.placeholder")}
            value={searchFieldInput.paymentId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "paymentId")}
            onfocusFn={() => textInputOnFocus("paymentId")}
            onblurFn={() => textInputOnBlur("paymentId")}
            sxParams={{ textFocus: focus.paymentId }}
            disabled={isInputDisabled("paymentId")}
          />
          {/* <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.merchantId.label")}
            placeholder={t(PAGE_LOCALE_KEY + "search.merchantId.placeholder")}
            value={searchFieldInput.merchantId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "merchantId")}
            onfocusFn={() => textInputOnFocus("merchantId")}
            onblurFn={() => textInputOnBlur("merchantId")}
            sxParams={{ textFocus: focus.merchantId }}
            disabled={isInputDisabled("merchantId")}
          /> */}

          <Datepicker
            label={t(PAGE_LOCALE_KEY + "search.reportedStartDate.label")}
            errorMessage={
              error.invalidReportedDateRange
                ? t(PAGE_LOCALE_KEY + "search.reportedStartDate.errorMessage.2")
                : error.tooLargeReportedDateRange
                  ? t(
                      PAGE_LOCALE_KEY +
                        "search.reportedStartDate.errorMessage.3",
                    )
                  : error.invalidReportedStartDate
                    ? t(
                        PAGE_LOCALE_KEY +
                          "search.reportedStartDate.errorMessage.4",
                      )
                    : t(
                        PAGE_LOCALE_KEY +
                          "search.reportedStartDate.errorMessage.1",
                      )
            }
            type="start"
            value={searchFieldInput.reportedStartDate}
            limitValue={searchFieldInput.reportedEndDate || dayjs()}
            minDate={dayjs("2022-01-01")}
            onchangeFn={reportedDateOnChange}
            onfocusFn={() => textInputOnFocus("reportedStartDate")}
            onblurFn={() =>
              textInputOnBlur("reportedStartDate", dateFormatValidator)
            }
            sxParams={{
              datepickerFocus: focus.reportedStartDate,
              datepickerError:
                error.reportedStartDate ||
                error.invalidReportedStartDate ||
                error.invalidReportedDateRange ||
                error.tooLargeReportedDateRange,
            }}
            disabled={isInputDisabled("reportedStartDate")}
          />
          <Datepicker
            label={t(PAGE_LOCALE_KEY + "search.reportedEndDate.label")}
            errorMessage={
              error.invalidReportedDateRange
                ? t(PAGE_LOCALE_KEY + "search.reportedEndDate.errorMessage.2")
                : error.tooLargeReportedDateRange
                  ? t(PAGE_LOCALE_KEY + "search.reportedEndDate.errorMessage.3")
                  : error.invalidReportedEndDate
                    ? t(
                        PAGE_LOCALE_KEY +
                          "search.reportedEndDate.errorMessage.4",
                      )
                    : t(
                        PAGE_LOCALE_KEY +
                          "search.reportedEndDate.errorMessage.1",
                      )
            }
            type="end"
            value={searchFieldInput.reportedEndDate}
            limitValue={
              searchFieldInput.reportedStartDate || dayjs("2022-01-01")
            }
            maxDate={dayjs()}
            onchangeFn={reportedDateOnChange}
            onfocusFn={() => textInputOnFocus("reportedEndDate")}
            onblurFn={() =>
              textInputOnBlur("reportedEndDate", dateFormatValidator)
            }
            sxParams={{
              datepickerFocus: focus.reportedEndDate,
              datepickerError:
                error.reportedEndDate ||
                error.invalidReportedEndDate ||
                error.invalidReportedDateRange ||
                error.tooLargeReportedDateRange,
            }}
            disabled={isInputDisabled("reportedEndDate")}
          />

          <Selection
            label={t(PAGE_LOCALE_KEY + "search.status.label")}
            placeholder={t(PAGE_LOCALE_KEY + "search.status.placeholder")}
            selectionValue={searchFieldInput.status}
            onchangeFn={(e) => textInputOnChange(e, "status")}
            onfocusFn={() => textInputOnFocus("status")}
            onblurFn={() => textInputOnBlur("status")}
            options={ChargebackStatusOptions("non-closed")}
            sxParams={{ selectionFocus: focus.status }}
            disabled={isInputDisabled("status")}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  const navigateToDetail = (data) => {
    const path = "/compliance/disputeDetail/DisputeDetail";
    const payload = {
      name: Compliance.find((tab) => tab.path === path).tabName + data.id,
      path: path,
      state: {
        id: data.id,
        createFlag: false,
      },
    };
    redirectFn("redirectPage", payload);
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <FunctionalButtonBar
          loading={loading}
          createBtn
          handleCreate={() => {
            const payload = {
              name: "Create Dispute",
              path: "/compliance/disputeDetail/DisputeDetail",
            };
            redirectFn("redirectPage", payload);
          }}
          refreshBtn
          handleRefresh={handleSearch}
          columnDisplayBtn
          columnDisplayProps={{
            columns,
            columnsArray,
            setColumnsArray,
            rememberPreference: "compliance-dispute",
          }}
        />
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            // TODO column sorting
            <Datatable
              navigateToDetail={navigateToDetail}
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}

          {searching && <Spin type={"search"} />}
        </div>
      </div>
    </div>
  );
};

export default React.memo(DisputeList);
